import React from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import Header from "../components/Header";
import Footer from "../components/Footer";
import blogPosts from "../data/blogPosts";
import conditionContent from "../data/conditionContent";

function pickLocale(post, lang) {
  return (post.i18n && post.i18n[lang]) || (post.i18n && post.i18n.en) || {};
}

function formatDate(iso, lang) {
  const date = new Date(iso);
  const localeMap = { en: "en-US", de: "de-DE", es: "es-ES", ka: "ka-GE" };
  return date.toLocaleDateString(localeMap[lang] || "en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

/**
 * Parses markdown-style links inside paragraph text and returns an array of
 * React nodes. `[label](/path)` → <Link>; `[label](https://...)` → <a>.
 */
function renderInlineText(text) {
  const parts = [];
  const regex = /\[([^\]]+)\]\(([^)]+)\)/g;
  let lastIndex = 0;
  let match;
  let key = 0;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }
    const isInternal = match[2].startsWith("/");
    if (isInternal) {
      parts.push(
        <Link key={`link-${key++}`} to={match[2]} className="blog-inline-link">
          {match[1]}
        </Link>
      );
    } else {
      parts.push(
        <a
          key={`link-${key++}`}
          href={match[2]}
          className="blog-inline-link"
          target="_blank"
          rel="noopener noreferrer"
        >
          {match[1]}
        </a>
      );
    }
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }
  return parts;
}

function renderBlock(block, idx, t) {
  switch (block.type) {
    case "p":
      return <p key={idx} className="blog-p">{renderInlineText(block.text)}</p>;
    case "h2":
      return <h2 key={idx} className="blog-h2">{block.text}</h2>;
    case "h3":
      return <h3 key={idx} className="blog-h3">{block.text}</h3>;
    case "ul":
      return (
        <ul key={idx} className="blog-ul">
          {block.items.map((item, i) => (
            <li key={i}>{renderInlineText(item)}</li>
          ))}
        </ul>
      );
    case "ol":
      return (
        <ol key={idx} className="blog-ol">
          {block.items.map((item, i) => (
            <li key={i}>{renderInlineText(item)}</li>
          ))}
        </ol>
      );
    case "blockquote":
      return (
        <blockquote key={idx} className="blog-blockquote">
          {renderInlineText(block.text)}
        </blockquote>
      );
    case "signature":
      return <p key={idx} className="blog-signature">{block.text}</p>;
    case "cta":
      return (
        <section key={idx} className="condition-cta-section blog-cta">
          <h2>{t("blog.cta_title")}</h2>
          <a
            href="https://apps.apple.com/app/id6755364999"
            className="condition-cta-btn"
          >
            {t("blog.cta_button")}
          </a>
          <p className="condition-cta-sub">{t("blog.cta_sub")}</p>
        </section>
      );
    default:
      return null;
  }
}

export default function BlogPost() {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || "en").split("-")[0];

  const post = blogPosts.find((p) => p.slug === slug);

  if (!post) {
    return (
      <>
        <Header />
        <main className="blog-page">
          <div className="container" style={{ padding: "80px 20px", textAlign: "center" }}>
            <h1>404</h1>
            <p>
              <Link to="/blog">← {t("blog.back_to_articles")}</Link>
            </p>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const loc = pickLocale(post, lang);
  const canonicalUrl = `https://ninoa.space/blog/${post.slug}`;
  const pageTitle = `${loc.title} | Ninoa`;

  const relatedConditions = (post.relatedConditionSlugs || [])
    .map((s) => conditionContent.find((c) => c.slug === s))
    .filter(Boolean)
    .slice(0, 4);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: loc.title,
    description: loc.metaDescription,
    inLanguage: lang,
    author: {
      "@type": "Person",
      name: post.author,
    },
    publisher: {
      "@type": "Organization",
      name: "Ninoa",
      logo: {
        "@type": "ImageObject",
        url: "https://ninoa.space/logo512.png",
      },
    },
    datePublished: post.publishedAt,
    dateModified: post.publishedAt,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": canonicalUrl,
    },
    url: canonicalUrl,
  };

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={loc.metaDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={loc.metaDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="article" />
        <meta property="article:published_time" content={post.publishedAt} />
        <meta property="article:author" content={post.author} />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={loc.metaDescription} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <Header />

      <main className="blog-page">
        <div className="container blog-post-inner">
          <Link to="/blog" className="condition-back-link">
            <span aria-hidden="true" className="condition-back-arrow">←</span>
            <span>{t("blog.back_to_articles")}</span>
          </Link>

          <article className="blog-article">
            <header className="blog-post-header">
              <div className="blog-card-meta">
                <span className="blog-card-category">{loc.category}</span>
                <span className="blog-card-dot">·</span>
                <span className="blog-card-date">{formatDate(post.publishedAt, lang)}</span>
                <span className="blog-card-dot">·</span>
                <span className="blog-card-read">
                  {post.readingTimeMin} {t("blog.min_read")}
                </span>
              </div>
              <h1 className="blog-post-title">{loc.title}</h1>
              <p className="blog-post-author">{t("blog.by_author", { author: post.author })}</p>
            </header>

            <div className="blog-post-body">
              {(loc.body || []).map((block, i) => renderBlock(block, i, t))}
            </div>
          </article>

          {relatedConditions.length > 0 && (
            <section className="condition-related">
              <h2 className="condition-related-title">{t("blog.related_conditions_title")}</h2>
              <p className="condition-related-sub">{t("blog.related_conditions_subtitle")}</p>
              <div className="condition-related-grid">
                {relatedConditions.map((c) => (
                  <Link key={c.slug} to={`/${c.slug}`} className="condition-related-card">
                    <span className="condition-related-name">{c.name}</span>
                    <span className="condition-related-cta">
                      {t("blog.learn_more")} <span aria-hidden="true">→</span>
                    </span>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}
